import { queryOptions, useQuery } from '@tanstack/react-query'
import { minutesToMilliseconds } from 'date-fns/minutesToMilliseconds'
import { useUser } from '@/features/account/services/User/hooks'
import { usePersona } from '@/features/business/hooks/usePersona'
import {
  type AviatorOrder,
  type IngestType,
  type OrdersHistory,
  type Order,
  type OrderTrackingResponse,
} from '@/features/shop/services/Orders/types'
import {
  fetchActiveOrders,
  fetchBusinessOrderHistory,
  fetchIncompleteOrders,
  fetchOrderEngagement,
  fetchOrderTracking,
  fetchOrdersHistory,
} from '@/features/shop/services/Orders/fetchers'
import {
  lastDeliveredOrderQueryOptions,
  orderQueryOptions,
} from '@/features/shop/services/Orders/queryOptions'
import { secondsToMilliseconds } from 'date-fns/secondsToMilliseconds'
import { useCallback } from 'react'
import { transformOrderTrackingProduct } from '@/features/shop/services/Orders/utils'
import { useIsOneOfPages } from '@/features/shared/hooks/useIsPage'
import { postOrderEligiblePages } from '@/features/shop/services/Orders/constants'
import { getDateRangeForBusinessOrderHistory } from '@/features/account/services/Business/utils'
import { type DateRangeValue } from '@/features/account/components/BusinessToolsPage/ExportReceipts/types'
import { useSession } from '@/features/authentication/utils/authentication/hooks/useSession'

export const OrdersQuery = 'Orders'
export const ActiveOrdersQuery = 'Active orders'
export const UpdateOrderMutation = 'Update order'

export type ActiveOrdersQueryKey = readonly [typeof ActiveOrdersQuery, string | undefined]
const activeOrdersQueryOptions = (activeGroupId: string | undefined) =>
  queryOptions({
    queryKey: [ActiveOrdersQuery, activeGroupId] as const,
    queryFn: fetchActiveOrders,
  })

export const useQueryActiveOrders = () => {
  const hasPlacedFirstOrder = Boolean(useUser()?.has_placed_marketplace_order)
  const { activeGroup } = usePersona()
  const activeGroupId = activeGroup?.id

  return useQuery({
    ...activeOrdersQueryOptions(activeGroupId),
    placeholderData: (prevData) => prevData,
    enabled: Boolean(hasPlacedFirstOrder),
    select: useCallback(
      (orders: AviatorOrder[]) =>
        orders.filter((order) =>
          activeGroupId ? order.customer_group_id === activeGroupId : !order.customer_group_id,
        ),
      [activeGroupId],
    ),
  })
}

const ordersHistoryQueryOptions = queryOptions({
  queryKey: [OrdersQuery] as const,
  queryFn: fetchOrdersHistory,
})

const ordersHistorySelect = (response: OrdersHistory) => ({
  upcomingOrders: [...response.upcomingOrders].sort((a, b) => b.id - a.id),
  pastOrders: [...response.pastOrders].sort((a, b) => b.id - a.id),
})

export const useQueryOrdersHistory = () => {
  const { userId } = useSession()

  return useQuery({
    ...ordersHistoryQueryOptions,
    enabled: Boolean(userId),
    select: ordersHistorySelect,
  })
}

export const useQueryLastDeliveredOrder = () => {
  const { userId } = useSession()
  const hasPlacedFirstOrder = Boolean(useUser()?.has_placed_marketplace_order)
  const shouldEnable = Boolean(hasPlacedFirstOrder && userId)

  return useQuery({
    ...lastDeliveredOrderQueryOptions,
    enabled: shouldEnable,
    // this is to ensure useQuery does not do any deep object comparison
    structuralSharing: false,
  })
}

export const useQueryOrder = (
  id: number,
  ingestType: IngestType = 'marketplace',
  options: {
    enabled?: boolean
    refetchInterval?: number
  } = { enabled: true },
) => {
  const { userId } = useSession()

  return useQuery({
    ...orderQueryOptions(id, ingestType),
    ...options,
    enabled: Boolean(id && options.enabled && userId),
  })
}

export const ORDER_TRACKING_QUERY_KEY = 'order tracking'

const orderTrackingQueryOptions = (orderId: number) => {
  return queryOptions({
    queryKey: [ORDER_TRACKING_QUERY_KEY, orderId] as const,
    queryFn: fetchOrderTracking,
  })
}
export type OrderTrackingQueryKey = readonly [string, number]

export const useQueryOrderTracking = (orderId: number, orderStatus: Order['status']) => {
  return useQuery({
    ...orderTrackingQueryOptions(orderId),
    // we would like members or have realtime experience until we get the websockets
    // average rate for shoppers to add products is around 85seconds, hence 90secs refetchinterval when order status is shopping
    ...(orderStatus === 'shopping' && {
      refetchInterval: secondsToMilliseconds(90),
    }),
    select: useCallback(
      (data: OrderTrackingResponse) => transformOrderTrackingProduct(data, orderStatus),
      [orderStatus],
    ),
  })
}

const OrderEngagementQuery = 'Order Engagement'

export type OrderEngagementQueryKey = readonly [typeof OrderEngagementQuery, { orderId: number }]

export const useQueryOrderEngagementETA = (orderId: number, isEnabled: boolean) => {
  return useQuery({
    queryKey: [OrderEngagementQuery, { orderId }] as const,
    queryFn: fetchOrderEngagement,
    refetchInterval: minutesToMilliseconds(2),
    enabled: Boolean(orderId) && isEnabled,
  })
}

export const incompleteOrdersQueryOptions = queryOptions({
  queryKey: ['incomplete orders'] as const,
  queryFn: fetchIncompleteOrders,
  staleTime: minutesToMilliseconds(15),
})

export const useQueryIncompleteOrders = () => {
  const { id, has_placed_marketplace_order } = useUser() ?? {}
  const isEligiblePage = useIsOneOfPages(postOrderEligiblePages)
  return useQuery({
    ...incompleteOrdersQueryOptions,
    enabled: Boolean(id && has_placed_marketplace_order && isEligiblePage),
  })
}

export const BusinessOrderHistoryQuery = 'BusinessOrderHistory'
export type BusinessOrderHistoryQueryKey = readonly [
  typeof BusinessOrderHistoryQuery,
  { startDate: string; endDate: string },
]

const businessOrderHistoryQueryOptions = (startDate: string, endDate: string) =>
  queryOptions({
    queryKey: [BusinessOrderHistoryQuery, { startDate, endDate }] as const,
    queryFn: fetchBusinessOrderHistory,
  })

export const useQueryBusinessOrderHistory = (range: DateRangeValue) => {
  const { isBusinessUser } = usePersona()
  const { startDate, endDate } = getDateRangeForBusinessOrderHistory(range)
  return useQuery({
    ...businessOrderHistoryQueryOptions(startDate, endDate),
    enabled: isBusinessUser,
  })
}
